/**
 * FocusResumeCoordinator — single tab-resume → staggered cache invalidation.
 *
 * 2026-05-27 — added during idle-tab-reconnect hardening. Pre-fix every
 * `refetchOnWindowFocus: true` query fired in the same JS macrotask when the
 * tab regained focus (5+ hooks across the app), producing a request burst
 * that blew the global 500/15min rate limiter even after the
 * `shouldSkipStandardRateLimit` skip-list bumps. The QueryClient default is
 * now `refetchOnWindowFocus: false` (see App.tsx) and this coordinator is the
 * single replacement: it invalidates the auth-cascade queries IMMEDIATELY
 * (cheap, skip the limiter, gate __root.tsx redirects) and defers the
 * everything-else refetches via TanStack's natural staleTime-driven path.
 *
 * Mounted once at __root.tsx. Side-effect only — returns null.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTabVisibility } from '@/hooks/use-tab-visibility';
import { queryKeys } from '@/lib/query-keys';

export function FocusResumeCoordinator(): null {
  const { lastResumedAt } = useTabVisibility();
  const queryClient = useQueryClient();
  const lastHandledRef = useRef<number | null>(null);

  useEffect(() => {
    if (lastResumedAt == null) return;
    if (lastHandledRef.current === lastResumedAt) return;
    lastHandledRef.current = lastResumedAt;

    // Tier 1 (immediate, cheap, skip-listed on the server): auth-cascade
    // queries that gate redirect decisions in __root.tsx. Stale data here
    // produces user-visible misroutes (e.g. wrong tab, false billing-lock),
    // so we accept the synchronous refetch.
    queryClient.invalidateQueries({
      queryKey: ['admin', 'me', 'status'],
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: ['users', 'me', 'admin-status'],
      exact: false,
    });
    // Match any user's billing-usage cache (the key is user-scoped, so we
    // invalidate by the `['billing','usage']` prefix instead of a specific
    // userId — TanStack matches all child keys).
    queryClient.invalidateQueries({
      queryKey: [...queryKeys.billing.all, 'usage'],
      exact: false,
    });

    // Tier 2 (deferred, lower priority): user-state queries the cascade
    // doesn't gate on. 800ms buffer keeps them out of the same macrotask as
    // the WS reconnect probe + Tier 1, smoothing the spike.
    const timer = window.setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.shares.unreadCount(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.user.profile(),
      });
    }, 800);

    return () => window.clearTimeout(timer);
  }, [lastResumedAt, queryClient]);

  return null;
}
